import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { type RefObject, useEffect, useRef, useState } from "react";
import { QuoteMatchMap, QuoteMatcher } from "../gradeValidationHelpers.js";
import type { CallPosition } from "../teammateAccountCallOutlineNavigation.js";
import { scrollToFragmentElement } from "./snippetSharingHelpers.js";
import type { FocusedSnippetState } from "./useTranscriptSnippetSharing.js";

/**
 * Hook that handles the `quote` URL query parameter.
 *
 * Uses {@link QuoteMatcher} to fuzzy-match the quoted text against the
 * recordings, seeks the audio player, and scrolls the transcript to the
 * matched fragment — the same UX as a shared snippet link.
 */
export function useTranscriptQuoteFromUrl(params: {
  containerRef: RefObject<HTMLElement | null>;
  recordings: readonly AccountGradingRecording[];
  seek: (sourceKey: string, time: number) => void;
  scrollToCallPosition?: (position: CallPosition) => void;
}) {
  const { containerRef, recordings, seek, scrollToCallPosition } = params;

  const [activeSnippet, setActiveSnippet] = useState<
    FocusedSnippetState | undefined
  >();

  const handleTranscriptQuote = useEffectEvent(() => {
    const transcriptQuote = new URLSearchParams(window.location.search).get(
      "quote",
    );
    if (!transcriptQuote) return;

    const quoteId = "transcript_quote";
    // Scope matching to the snippet's recording when provided, so a search snippet
    // resolves within the call it came from rather than the first global match.
    const recordingId = new URLSearchParams(window.location.search).get(
      "recordingId",
    );
    const scopedRecordings = recordingId
      ? recordings.filter((recording) => recording.id === recordingId)
      : recordings;
    const matcher = QuoteMatcher.new(scopedRecordings);
    const matches = matcher.findMatches([
      {
        type: "quote",
        id: quoteId,
        short: transcriptQuote,
        long: transcriptQuote,
      },
    ]);

    const quoteMatches = QuoteMatchMap.findByQuote(matches, quoteId);
    if (!quoteMatches || quoteMatches.length === 0) return;

    const firstMatch = quoteMatches[0];
    if (!firstMatch) return;
    const recording = recordings.find((r) => r.id === firstMatch.recordingId);
    if (!recording?.transcript) return;

    const fragment =
      recording.transcript.fragments[firstMatch.recordingFragmentIndex];
    if (!fragment) return;

    seek(firstMatch.recordingId, fragment.start_time / 1000);

    scrollToCallPosition?.({
      recordingId: firstMatch.recordingId,
      timestamp: fragment.start_time,
    });

    const container = containerRef.current;
    if (container) {
      scrollToFragmentElement(
        container,
        firstMatch.recordingId,
        firstMatch.recordingFragmentIndex,
      );
    }

    setActiveSnippet({
      recordingId: firstMatch.recordingId,
      startFragmentIndex: firstMatch.recordingFragmentIndex,
      endFragmentIndex: firstMatch.recordingFragmentIndex,
      startCharOffset: firstMatch.inFragmentIndex,
      endCharOffset: firstMatch.inFragmentIndex + firstMatch.length,
    });
  });

  const didHandleTranscriptQuoteRef = useRef(false);
  useEffect(() => {
    // If we already did this, don't do it again.
    if (didHandleTranscriptQuoteRef.current) {
      return;
    }

    // If recordings haven't loaded yet, wait until they have.
    if (recordings.length === 0) {
      return;
    }

    try {
      handleTranscriptQuote();
    } finally {
      didHandleTranscriptQuoteRef.current = true;
    }
  }, [recordings, handleTranscriptQuote]);

  return { activeSnippet };
}
